import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import EsriLoaderReact from 'esri-loader-react';
import proj4 from 'proj4';
import React from 'react';
import PropTypes from 'prop-types';
import { ComponentHeader } from '../../../components/component-header/component-header';
import NOB from '../../../constants/language-strings';
import * as CONSTANT from '../constants';
import './map.scss';
import { PebbleSpinner, PurPurButton } from 'components';
import { Icon } from '@purpurds/purpur/icon';
import { icons } from 'theme/icons';
import styled from 'styled-components';
import { purpurShadowMd } from '@purpurds/tokens/shadow/variables';
import { purpurColorRed600, purpurColorTransparentWhite800, } from '@purpurds/tokens/color/variables';
const StyledButton = styled(PurPurButton) `
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: flex-end;
  justify-self: flex-end;
  box-shadow: ${purpurShadowMd};
  width: 48px;
  height: 48px;
  margin: 5px 5px 0 0;
  padding: 0;
  z-index: 2;
`;
const StyledIcon = styled(Icon) `
  margin-right: 10px;
  color: ${purpurColorRed600};
  cursor: pointer;
`;
const EsriLoader = ({ address, handleExpand, id, isFetching, position, shouldExpand, token }) => {
    if (isFetching) {
        return (_jsx("div", { className: "map__container--minimized", children: _jsx(PebbleSpinner, {}) }));
    }
    let customerPosition = true;
    if (Number.isNaN(position.latitude) || Number.isNaN(position.longitude)) {
        customerPosition = false;
    }
    let UTM33;
    if (customerPosition) {
        UTM33 = proj4(CONSTANT.WGS84, CONSTANT.UTM, [position.longitude, position.latitude]);
    }
    return (_jsxs("div", { className: shouldExpand ? 'map__container--fullscreen' : 'map__container--minimized', children: [_jsx(EsriLoaderReact, { modulesToLoad: [
                    'esri/config',
                    'esri/identity/IdentityManager',
                    'esri/WebMap',
                    'esri/views/MapView',
                    'esri/tasks/Locator',
                    'esri/Graphic',
                    'esri/layers/GraphicsLayer',
                    'esri/Viewpoint',
                    'esri/geometry/Point',
                ], onReady: ({ loadedModules: [esriConfig, IdentityManager, WebMap, MapView, Locator, Graphic, GraphicsLayer, Viewpoint, Point,], containerNode, }) => {
                    const mapUrl = process.env.MAP_URL
                        ? process.env.MAP_URL
                        : `${document.location.protocol}//${document.location.host}`;
                    esriConfig.request.interceptors.push({
                        urls: 'https://geosam.get.no',
                        before(params) {
                            params.url = params.url.replace('/portal/', '/ias/'); // eslint-disable-line
                            params.url = params.url.replace('https://geosam.get.no/', `${mapUrl}/`);
                            return null;
                        },
                    });
                    esriConfig.portalUrl = `https://geosam.get.no/ias`; // eslint-disable-line
                    IdentityManager.registerToken({
                        server: `${mapUrl}/server/rest/services`,
                        token,
                    });
                    IdentityManager.registerToken({
                        server: `${mapUrl}/ias/sharing/rest`,
                        token,
                    });
                    IdentityManager.registerToken({
                        server: `https://geosam.get.no/portal`,
                        token,
                    });
                    const webmap = new WebMap({
                        portalItem: {
                            id: id,
                        },
                    });
                    const locator = new Locator();
                    locator.url = CONSTANT.GEOCODE_URL;
                    locator.countryCode = CONSTANT.NO;
                    if (customerPosition) {
                        new MapView({
                            container: containerNode,
                            ui: { components: [CONSTANT.ATTRIBUTION] },
                            viewpoint: new Viewpoint({
                                targetGeometry: new Point({
                                    x: UTM33[0],
                                    y: UTM33[1],
                                    spatialReference: { wkid: CONSTANT.WKID },
                                }),
                                scale: CONSTANT.SCALE,
                            }),
                            map: webmap,
                        });
                        const graphicsLayer = new GraphicsLayer();
                        webmap.add(graphicsLayer);
                        const point = {
                            type: 'point',
                            longitude: position.longitude,
                            latitude: position.latitude,
                        };
                        const simpleMarkerSymbol = {
                            type: 'simple-marker',
                            color: [226, 119, 40],
                            outline: {
                                color: [255, 255, 255],
                                width: 1,
                            },
                        };
                        const pointGraphic = new Graphic({
                            geometry: point,
                            symbol: simpleMarkerSymbol,
                        });
                        graphicsLayer.add(pointGraphic);
                    }
                    else {
                        locator
                            .addressToLocations({ address: { address } })
                            .then(res => new MapView({
                            container: containerNode,
                            ui: { components: [CONSTANT.ATTRIBUTION] },
                            viewpoint: new Viewpoint({
                                targetGeometry: res[0].location,
                                scale: CONSTANT.SCALE,
                            }),
                            map: webmap,
                        }))
                            .catch(error => error);
                    }
                } }), !shouldExpand ? (_jsx(StyledButton, { size: "sm", type: "button", variant: "tertiary-purple", onClick: handleExpand, style: {
                    position: 'absolute',
                    borderRadius: '4px',
                    backgroundColor: purpurColorTransparentWhite800,
                }, children: _jsx(Icon, { svg: icons.maximize, size: "md" }) })) : (_jsx(ComponentHeader, { isOnMap: true, rightIcon: _jsx(StyledIcon, { svg: icons.close, size: "lg", onClick: handleExpand }), mainHeading: NOB.MAP.MAP }))] }));
};
EsriLoader.propTypes = {
    address: PropTypes.string.isRequired,
    handleExpand: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    isFetching: PropTypes.bool.isRequired,
    position: PropTypes.objectOf(PropTypes.number).isRequired,
    shouldExpand: PropTypes.bool.isRequired,
    token: PropTypes.string.isRequired,
};
export default EsriLoader;
